/**
 * SegmentBlock Component
 * 
 * Individual draggable/resizable segment on the timeline.
 * Active segments are solid, inactive segments are hatched.
 */

import React, { useRef, useState, useEffect } from 'react';
import { ScheduledSegment } from '../../types/menuPlanning';
import '../../styles/menuPlanning.css';

interface SegmentBlockProps {
  segment: ScheduledSegment;
  pixelsPerSecond: number;
  onDragStart?: (segment: ScheduledSegment) => void;
  onDrag?: (segment: ScheduledSegment, newStartSec: number) => void;
  onDragEnd?: (segment: ScheduledSegment, newStartSec: number) => void;
  onResize?: (segment: ScheduledSegment, newDurationSec: number) => void;
  isConflict?: boolean;
}

export const SegmentBlock: React.FC<SegmentBlockProps> = ({
  segment,
  pixelsPerSecond,
  onDragStart,
  onDrag,
  onDragEnd,
  onResize,
  isConflict = false
}) => {
  const blockRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const leftPx = segment.startSec * pixelsPerSecond;
  const widthPx = segment.durationSec * pixelsPerSecond;

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isResizing) return;
    
    setIsDragging(true);
    const rect = blockRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset(e.clientX - rect.left);
    }
    
    if (onDragStart) {
      onDragStart(segment);
    }
    
    e.preventDefault();
  };

  const handleResizeMouseDown = (e: React.MouseEvent, direction: 'left' | 'right') => {
    setIsResizing(true);
    e.stopPropagation();
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && onDrag) {
        const parentRect = blockRef.current?.parentElement?.getBoundingClientRect();
        if (parentRect) {
          const relativeX = e.clientX - parentRect.left - dragOffset;
          const newStartSec = Math.max(0, Math.round(relativeX / pixelsPerSecond));
          onDrag(segment, newStartSec);
        }
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (isDragging && onDragEnd) {
        const parentRect = blockRef.current?.parentElement?.getBoundingClientRect();
        if (parentRect) {
          const relativeX = e.clientX - parentRect.left - dragOffset;
          const newStartSec = Math.max(0, Math.round(relativeX / pixelsPerSecond));
          onDragEnd(segment, newStartSec);
        }
      }
      
      setIsDragging(false);
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, pixelsPerSecond, segment, onDrag, onDragEnd]);

  const className = `segment-block ${
    segment.type === 'active' ? 'segment-active' : 'segment-inactive'
  } ${isConflict ? 'segment-conflict' : ''}`;

  return (
    <div
      ref={blockRef}
      className={className}
      style={{
        left: `${leftPx}px`,
        width: `${widthPx}px`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      title={`${segment.title || 'Untitled'} (${segment.type}): ${Math.floor(segment.durationSec / 60)}m ${segment.durationSec % 60}s`}
    >
      {widthPx > 60 && (
        <span>
          {segment.title && segment.title.length > 20 
            ? segment.title.substring(0, 17) + '...' 
            : (segment.title || 'Untitled')}
        </span>
      )}
    </div>
  );
};

export default SegmentBlock;


